// Modal de subida de vídeo para el análisis de movimiento: ejercicio de la
// biblioteca (buscador), peso y repeticiones (opcionales) + archivo. Todo ello
// se guarda codificado en el nombre del objeto de Storage
// (<ts>__<exerciseId>__<weight|x>__<reps|x>__<archivo>): esta feature no tiene
// tabla propia.
import { useRef, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { FilmIcon } from "@heroicons/react/24/outline";
import ExercisePicker from "./ExercisePicker";

export default function UploadVideoModal({ isOpen, onClose, onUpload, uploading }) {
  const fileInputRef = useRef(null);
  const [exercise, setExercise] = useState(null);
  const [weight, setWeight] = useState("");
  const [reps, setReps] = useState("");
  const [rmPercent, setRmPercent] = useState("");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");

  const reset = () => {
    setExercise(null);
    setWeight("");
    setReps("");
    setRmPercent("");
    setFile(null);
    setError("");
  };

  const handleClose = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!exercise) {
      setError("Choose the exercise this video belongs to.");
      return;
    }
    if (!file) {
      setError("Choose a video file.");
      return;
    }
    setError("");
    const ok = await onUpload({
      exercise,
      file,
      weight: weight === "" ? null : Number(weight),
      reps: reps === "" ? null : Number(reps),
      rmPercent: rmPercent === "" ? null : Number(rmPercent),
    });
    if (ok) {
      reset();
      onClose();
    } else {
      setError("Upload failed. Please try again.");
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel className="relative w-full transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:max-w-lg sm:p-6">
            <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
              Upload video
            </DialogTitle>

            <div className="mt-4">
              <ExercisePicker value={exercise} onChange={setExercise} disabled={uploading} />
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3">
              <div>
                <label htmlFor="upload-weight" className="block text-sm font-medium text-gray-700">
                  Weight (kg)
                </label>
                <input
                  id="upload-weight"
                  type="number"
                  min="0"
                  step="0.5"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  disabled={uploading}
                  placeholder="optional"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-40 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="upload-reps" className="block text-sm font-medium text-gray-700">
                  Reps
                </label>
                <input
                  id="upload-reps"
                  type="number"
                  min="0"
                  step="1"
                  value={reps}
                  onChange={(e) => setReps(e.target.value)}
                  disabled={uploading}
                  placeholder="optional"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-40 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="upload-rm" className="block text-sm font-medium text-gray-700">
                  %RM
                </label>
                <input
                  id="upload-rm"
                  type="number"
                  min="0"
                  max="200"
                  step="0.5"
                  value={rmPercent}
                  onChange={(e) => setRmPercent(e.target.value)}
                  disabled={uploading}
                  placeholder="optional"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-40 sm:text-sm"
                />
              </div>
            </div>

            <div className="mt-4">
              <span className="block text-sm font-medium text-gray-700">Video file</span>
              <div className="mt-1 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40"
                >
                  <FilmIcon className="h-5 w-5" />
                  Choose file
                </button>
                <span className="truncate text-sm text-gray-500">
                  {file ? file.name : "No file selected"}
                </span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(e) => {
                  setFile(e.target.files?.[0] || null);
                  e.target.value = "";
                }}
              />
            </div>

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <div className="mt-5 grid grid-cols-1 gap-2 sm:mt-6 sm:grid-flow-row-dense sm:grid-cols-2">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={uploading}
                className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:bg-gray-300 sm:col-start-2"
              >
                {uploading ? "Uploading…" : "Upload"}
              </button>
              <button
                type="button"
                onClick={handleClose}
                disabled={uploading}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40 sm:col-start-1 sm:mt-0"
              >
                Cancel
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
